import { Component, inject, OnInit } from '@angular/core';
import { map, Observable } from 'rxjs';
import { PublicAppSection } from '../../core/store/ui/ui.state';
import { Store } from '@ngrx/store';
import { fromStore } from '../../core/store';
import { selectUiState } from '../../core/store/reducers';
import { Vendor } from '../../core/models/entities/vendor';
import { LocalizedUrlService } from '../../core/services/localized-url.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-footer',
  template: `
    <div class="bg-secondary-900 py-20 text-white">
      <div class="container">
        <div class="grid grid-cols-1 lg:grid-cols-3">
          <div *ngIf="vendor$ | async; else ercosInfo" class="col-span-1">
            <div class="font-bold uppercase">{{ (vendor$ | async)?.name }}</div>
            <div>{{ (vendor$ | async)?.address }}</div>
            <div
              >{{ (vendor$ | async)?.zip }} {{ (vendor$ | async)?.city }}</div
            >
          </div>
          <ng-template #ercosInfo>
            <div class="col-span-1">
              <a href="https://www.ercos.ch" target="_blank">
                <img
                  class="w-14"
                  src="/assets/img/ercos-gris.svg"
                  [alt]="'footer.developed-by-ercos' | transloco"
                  [title]="'footer.developed-by-ercos' | transloco"
                />
              </a>
              <div class="pt-3 font-bold uppercase">Ercos SA</div>
              <div>Chemin en Cormistaux 92A</div>
              <div>1040 Echallens</div>
            </div>
          </ng-template>
          <div class="col-span-1 mt-8 flex lg:mt-0 lg:justify-center">
            <div *ngIf="B2C$ | async">
              <div
                ><a
                  *ngIf="(vendor$ | async)?.general_terms_public_url"
                  [href]="(vendor$ | async)?.general_terms_public_url"
                  target="_blank"
                  >{{ 'footer.general-terms' | transloco }}</a
                ></div
              >
              <div
                ><a
                  [href]="privacyPolicyUrl"
                  target="_blank"
                  rel="noopener"
                  download
                  >{{ 'footer.privacy-policy' | transloco }}</a
                ></div
              >
              <div
                ><a
                  href="#"
                  class="biskoui-button-show-banner"
                  (click)="$event.preventDefault()"
                  >{{ 'footer.privacy-settings' | transloco }}</a
                ></div
              >
            </div>
            <div *ngIf="(B2C$ | async) === false">
              <div
                ><a routerLink="/conditions-generales" target="_blank"
                  >{{ 'footer.general-terms' | transloco }}</a
                ></div
              >
              <div
                ><a
                  [href]="privacyPolicyUrl"
                  target="_blank"
                  rel="noopener"
                  download
                  >{{ 'footer.privacy-policy' | transloco }}</a
                ></div
              >
              <div
                ><a
                  href="#"
                  class="biskoui-button-show-banner"
                  (click)="$event.preventDefault()"
                  >{{ 'footer.privacy-settings' | transloco }}</a
                ></div
              >
              <div
                ><a [routerLink]="localizedUrl.link('permissions')">{{
                  'footer.roles-access' | transloco
                }}</a></div
              >
            </div>
          </div>

          <div
            *ngIf="vendor$ | async; else ercosContactDetails"
            class="col-span-1 mt-8 flex lg:mt-0 lg:justify-end"
          >
            <div class="space-y-2">
              <div *ngIf="(vendor$ | async)?.email">
                <a
                  class="no-underline"
                  href="mailto:{{ (vendor$ | async)?.email }}"
                >
                  <i class="fal fa-paper-plane mr-1"></i>
                  {{ (vendor$ | async)?.email }}
                </a>
              </div>
              <div *ngIf="(vendor$ | async)?.phone">
                <a
                  class="no-underline"
                  href="tel:{{ (vendor$ | async)?.phone }}"
                >
                  <i class="fal fa-phone mr-1"></i>
                  {{ (vendor$ | async)?.phone }}
                </a>
              </div>
            </div>
          </div>
          <ng-template #ercosContactDetails>
            <div class="col-span-1 mt-8 flex lg:mt-0 lg:justify-end">
              <div>
                <div><a [routerLink]="localizedUrl.link('becomeHost')">{{ 'footer.become-host' | transloco }}</a></div>
                <div><a [routerLink]="localizedUrl.link('partners')">{{ 'footer.become-reseller' | transloco }}</a></div>
                <div><a [routerLink]="localizedUrl.link('search')">{{ 'footer.search-room' | transloco }}</a></div>
                <div><a [routerLink]="localizedUrl.link('contact')">{{ 'footer.contact' | transloco }}</a></div>
                <div class="mt-4">
                  <a [routerLink]="localizedUrl.authLink('login')">
                    <app-button variant="dark" [pill]="true">{{ 'footer.login' | transloco }}</app-button>
                  </a>
                </div>
              </div>
            </div>
          </ng-template>
        </div>

        <div *ngIf="B2C$ | async" class="mt-10 text-sm">
          <div>{{
            'footer.managed-by' | transloco: { name: (vendor$ | async)?.name }
          }}</div>
          <div
            class="mt-1"
            [innerHTML]="'footer.developed-by' | transloco"
          ></div>
        </div>
      </div>
    </div>
  `,
  standalone: false,
})
export class FooterComponent implements OnInit {
  B2C$: Observable<boolean> = this.store
    .select(fromStore.selectPublicAppSection)
    .pipe(
      map((publicAppSection: PublicAppSection) => {
        return publicAppSection === PublicAppSection.B2C;
      }),
    );
  vendor$: Observable<Vendor | null> = this.store
    .select(selectUiState)
    .pipe(
      map((uiState) =>
        uiState.publicAppSection === PublicAppSection.B2C
          ? uiState.vendor
          : null,
      ),
    );

  protected localizedUrl = inject(LocalizedUrlService);

  /** PDF statique de la politique de confidentialité (document Ercos). */
  protected privacyPolicyUrl = `${environment.appUrl}/assets/documents/Politique_de_confidentialité.pdf`;

  constructor(private store: Store) {}

  ngOnInit(): void {}
}
